import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { FormFooter } from "@/components/molecules/form-footer";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { SettingsSection } from "@/components/molecules/settings-section";
import {
  FieldCard,
  FieldEditor,
  type BaseField,
} from "@/components/molecules/field-editor";
import { Button } from "@/components/ui/button";
import {
  useMetadataConfig,
  useUpdateMetadataConfig,
} from "@/features/documents/hooks/use-metadata-config";
import type { MetadataField } from "@/features/documents/types";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVerticalIcon, PlusIcon, TagIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "@/features/utils/i18n";
import { PageLayout } from "../../layout";
import { GenerateFieldsDialog } from "./generate-fields-dialog";

export function MetadataConfigPage() {
  const { t } = useTranslation("documents");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: config, isLoading, isError, refetch } = useMetadataConfig();
  const updateConfig = useUpdateMetadataConfig();

  const [fields, setFields] = useState<MetadataField[]>([]);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize fields when config loads
  useEffect(() => {
    if (config?.fields?.fields) {
      setFields(config.fields.fields);
    }
  }, [config]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasChanges(true);
    }
  };

  const addField = () => {
    const newField: MetadataField = {
      id: `field_${Date.now()}`,
      label: "",
      type: "text",
      required: false,
    };
    setFields([...fields, newField]);
    setEditingFieldId(newField.id);
    setHasChanges(true);
  };

  const removeField = (id: string) => {
    setFields(fields.filter((f) => f.id !== id));
    setHasChanges(true);
  };

  const updateField = (id: string, updates: Partial<MetadataField>) => {
    setFields(fields.map((f) => (f.id === id ? { ...f, ...updates } : f)));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Validate fields
      const ids = new Set<string>();
      for (const field of fields) {
        if (!field.id || !field.label) {
          toast.error(t("documents.metadata.messages.missingIdOrLabel"));
          return;
        }
        if (ids.has(field.id)) {
          toast.error(
            t("documents.metadata.messages.duplicateId", { id: field.id }),
          );
          return;
        }
        ids.add(field.id);
      }

      await updateConfig.mutateAsync(fields);
      setHasChanges(false);
      toast.success(t("documents.metadata.messages.saved"));
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : t("documents.metadata.messages.saveError");
      toast.error(message);
    }
  };

  const handleGeneratedFields = (generatedFields: MetadataField[]) => {
    setFields([...fields, ...generatedFields]);
    setHasChanges(true);
  };

  // The edits go back to what is saved; leaving with them is `FormFooter`'s
  // question now, asked for every way out rather than for one button.
  const handleCancel = () => {
    setFields(config?.fields?.fields || []);
    setEditingFieldId(null);
    setHasChanges(false);
  };

  const backTo = { label: t("documents.title"), path: `/${clientId}/data` };

  if (isLoading)
    return <PageSkeleton width="narrow" blocks={["h-24", "h-72"]} />;

  // An empty editor over a config we could not read would save over it.
  if (isError) {
    return (
      <PageLayout
        title={t("documents.metadata.title")}
        backTo={backTo}
        width="narrow"
      >
        <ErrorState onRetry={refetch} />
      </PageLayout>
    );
  }

  const addButtons = (
    <>
      <GenerateFieldsDialog onFieldsGenerated={handleGeneratedFields} />
      <Button onClick={addField} variant="outline" size="sm">
        <PlusIcon className="size-4" />
        {fields.length === 0
          ? t("documents.metadata.fields.addManually")
          : t("documents.metadata.fields.add")}
      </Button>
    </>
  );

  return (
    <PageLayout
      title={t("documents.metadata.title")}
      description={t("documents.metadata.description")}
      backTo={backTo}
      width="narrow"
    >
      {/* One parent for the sections, so the first one drops its rule. */}
      <div>
        <SettingsSection title={t("documents.metadata.howItWorks.title")}>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{t("documents.metadata.howItWorks.define")}</li>
            <li>{t("documents.metadata.howItWorks.extract")}</li>
            <li>{t("documents.metadata.howItWorks.search")}</li>
          </ul>
        </SettingsSection>

        {/* Stacked: a field card needs the whole width. */}
        <SettingsSection
          stacked
          title={t("documents.metadata.fields.title")}
          description={t("documents.metadata.fields.count", {
            count: fields.length,
          })}
          actions={
            fields.length > 0 && (
              <div className="flex flex-wrap gap-2">{addButtons}</div>
            )
          }
        >
          {fields.length === 0 ? (
            <EmptyState
              icon={TagIcon}
              title={t("documents.metadata.fields.empty")}
              action={addButtons}
            />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <SortableFieldWrapper
                      key={field.id}
                      field={field}
                      index={index}
                      isEditing={editingFieldId === field.id}
                      onEdit={() => setEditingFieldId(field.id)}
                      onUpdate={(updates) => updateField(field.id, updates)}
                      onRemove={() => removeField(field.id)}
                      onDone={() => setEditingFieldId(null)}
                      canRemove={fields.length > 0}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </SettingsSection>
      </div>

      <FormFooter
        dirty={hasChanges}
        saving={updateConfig.isPending}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </PageLayout>
  );
}

interface SortableFieldWrapperProps {
  field: MetadataField;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onUpdate: (updates: Partial<MetadataField>) => void;
  onRemove: () => void;
  onDone: () => void;
  canRemove: boolean;
}

const SortableFieldWrapper = ({
  field,
  index,
  isEditing,
  onEdit,
  onUpdate,
  onRemove,
  onDone,
  canRemove,
}: SortableFieldWrapperProps) => {
  const { t } = useTranslation("documents");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-2">
      <button
        type="button"
        aria-label={t("documents.metadata.fields.reorder")}
        className="mt-4 flex cursor-grab items-start text-muted-foreground hover:text-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <DotsSixVerticalIcon className="size-5" />
      </button>
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <FieldEditor
            field={field as BaseField}
            index={index}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onDone={onDone}
            canRemove={canRemove}
          />
        ) : (
          <FieldCard
            field={field as BaseField}
            index={index}
            onClick={onEdit}
            onRemove={onRemove}
            canRemove={canRemove}
          />
        )}
      </div>
    </div>
  );
};
